import { ref } from 'vue';
import { defineStore } from 'pinia';
import { IGame, IGameMessage, IGameState, IEvent, IMessageBody, IMessageHeader } from '@/types';
import { cloneDeep } from 'lodash';
import { sendUpdate } from '@/api/requests';
import { AxiosResponse } from 'axios';
import { useQueueManager } from './queueManager';
import { useUserStore } from './user';
import { TGameStatus } from '@/constants';

export const useGameStateStore = defineStore('gameState', () => {
  const snapShot = ref<IGame>({
    gameId: 0,
    gameState: null,
    plays: [],
  });
  const gameState = ref<IGameState>({
    gameStatusId: 1,
    quarter: 1,
    gameClock: {
      minutes: 15,
      seconds: 0,
    },
    clockIsRunning: false,
    awayTeamScore: 0,
    homeTeamScore: 0,
    awayTeamTimeoutsLeft: 3,
    homeTeamTimeoutsLeft: 3,
    hasMediaDelay: false,
  });
  const notification = ref({
    active: false,
    success: true,
    text: '',
  });
  const loading = ref(false);
  const inDanger = ref(false);
  const preSecondHalfState = ref<{ snapShot: IGame; gameState: IGameState }>(null);

  const handleNotification = (success: boolean, message?: string) => {
    const text = message ?? `Event ${success ? 'fired successfully' : 'was unsuccessful'}`;
    notification.value = {
      active: true,
      success,
      text,
    };
  };

  const messageHeader = () => {
    const dateTime = new Date();
    const { getUserRole } = useUserStore();
    const header: IMessageHeader = {
      messageType: 1,
      scout: getUserRole(),
      clientTimestamp: dateTime.toISOString(),
    };
    return header;
  };

  const sendGameUpdate = () => {
    useQueueManager().enqueueGameStateUpdate(snapShot.value.gameId, gameState.value);
  };

  const fireGameEvent = async (
    eventTypeId: number,
    value: number | boolean,
    sendGameState?: boolean,
    withFeedback = false
  ): Promise<any> => {
    const header = messageHeader();
    const event: IEvent = {
      eventTypeId,
      value,
    };
    const body: IMessageBody = {
      gameId: snapShot.value.gameId,
      events: [{ ...event, value: `${event.value}` }],
    };
    const gameMessage: IGameMessage = {
      header,
      body,
    };

    if (sendGameState) {
      return useQueueManager().enqueueGameStateUpdate(snapShot.value.gameId, gameState.value, [
        { ...event, value: `${event.value}` },
      ]);
    }

    const response: AxiosResponse | false = await sendUpdate(gameMessage);
    if (withFeedback) {
      const success = response && response.status >= 200 && response.status < 300;
      return handleNotification(success);
    }
    return response;
  };

  const setDefaultState = () => {
    gameState.value = {
      gameStatusId: 1,
      quarter: 1,
      gameClock: {
        minutes: 15,
        seconds: 0,
      },
      clockIsRunning: false,
      awayTeamScore: 0,
      homeTeamScore: 0,
      awayTeamTimeoutsLeft: 3,
      homeTeamTimeoutsLeft: 3,
      hasMediaDelay: false,
    };
  };

  const setDefaultSnapshot = () => {
    snapShot.value = {
      gameId: 0,
      gameState: null,
      plays: [],
    };
  };

  const setGameStateFromSnapshot = (isForQaScout: boolean, state: IGameState) => {
    if (!isForQaScout) {
      gameState.value = state;
    } else {
      // For QA scout some properties will only be updated if clock not currently running
      // and not/or not pending blocking update.
      let tempGameClock = gameState.value.gameClock;
      let tempQuarter = gameState.value.quarter;
      let tempAwayScore = gameState.value.awayTeamScore;
      let tempHomeScore = gameState.value.homeTeamScore;
      let tempHomeTOs = gameState.value.homeTeamTimeoutsLeft;
      let tempAwayTOs = gameState.value.awayTeamTimeoutsLeft;

      if (!gameState.value.clockIsRunning && !useQueueManager().queue.isProcessingGameState) {
        tempGameClock = state.gameClock;
      }
      if (!useQueueManager().queue.isProcessingGameState) {
        tempQuarter = state.quarter;
        tempAwayScore = state.awayTeamScore;
        tempHomeScore = state.homeTeamScore;
        tempHomeTOs = state.homeTeamTimeoutsLeft;
        tempAwayTOs = state.awayTeamTimeoutsLeft;
      }

      gameState.value = {
        ...state,
        clockIsRunning: gameState.value.clockIsRunning,
        gameClock: tempGameClock,
        awayTeamScore: tempAwayScore,
        homeTeamScore: tempHomeScore,
        homeTeamTimeoutsLeft: tempHomeTOs,
        awayTeamTimeoutsLeft: tempAwayTOs,
        quarter: tempQuarter,
      };
    }
  };

  const setInDanger = (status: boolean) => {
    inDanger.value = status;
  };

  const setInitialState = () => {
    if (snapShot.value?.gameState) {
      gameState.value = cloneDeep(snapShot.value.gameState);
    } else {
      setDefaultState();
    }
  };

  const resetGameDefaults = () => {
    setDefaultSnapshot();
    setInitialState();
  };

  function resetClock() {
    const { gameClock } = gameState.value;
    gameClock.minutes = 15;
    gameClock.seconds = 0;
  }

  const stopClock = async () => {
    gameState.value.clockIsRunning = false;
    sendGameUpdate();
  };

  function runClock() {
    if (gameState.value.clockIsRunning) {
      const { gameClock } = gameState.value;
      if (
        gameClock.minutes < 0 ||
        gameClock.seconds < 0 ||
        (gameClock.minutes === 0 && gameClock.seconds === 0)
      ) {
        gameClock.minutes = 0;
        gameClock.seconds = 0;
        stopClock();
        return;
      }
      const { seconds } = gameClock;
      if (seconds === 0) {
        gameClock.minutes -= 1;
        gameClock.seconds = 59;
      } else {
        gameClock.seconds -= 1;
        if (gameClock.seconds === 0 && gameClock.minutes === 0) {
          stopClock();
        }
      }
      sendGameUpdate();
      setTimeout(runClock, 1000);
    }
  }

  const resetTimeouts = () => {
    gameState.value.homeTeamTimeoutsLeft = 3;
    gameState.value.awayTeamTimeoutsLeft = 3;
  };

  // Play scout-only action (FlipSwitch); must not push a game state update - only the clock scout owns that.
  const startSecondHalf = () => {
    preSecondHalfState.value = {
      snapShot: cloneDeep(snapShot.value),
      gameState: cloneDeep(gameState.value),
    };
  };

  // Play scout-only action (FlipSwitch); must not push a game state update - only the clock scout owns that.
  const undoStartSecondHalf = () => {
    if (!preSecondHalfState.value) return;

    snapShot.value = cloneDeep(preSecondHalfState.value.snapShot);
    gameState.value = cloneDeep(preSecondHalfState.value.gameState);
    preSecondHalfState.value = null;
  };

  const setOvertimeState = () => {
    gameState.value.homeTeamTimeoutsLeft = 1;
    gameState.value.awayTeamTimeoutsLeft = 1;
  };

  const startClock = () => {
    if (gameState.value.gameStatusId === TGameStatus.NOT_STARTED)
      gameState.value.gameStatusId = TGameStatus.IN_PROGRESS;

    const { minutes, seconds } = gameState.value.gameClock;
    if (minutes < 0 || seconds < 0 || (minutes === 0 && seconds === 0)) {
      gameState.value.gameClock.minutes = 0;
      gameState.value.gameClock.seconds = 0;
      return;
    }

    if (!gameState.value.clockIsRunning) {
      gameState.value.clockIsRunning = true;
      setTimeout(runClock, 1000);
    }
  };

  const useTimeout = async (isAway: boolean) => {
    gameState.value.clockIsRunning = false;
    if (isAway) {
      if (gameState.value.awayTeamTimeoutsLeft > 0) gameState.value.awayTeamTimeoutsLeft -= 1;
    } else if (gameState.value.homeTeamTimeoutsLeft > 0) gameState.value.homeTeamTimeoutsLeft -= 1;

    sendGameUpdate();
  };

  const setTimeouts = async (isAway: boolean, value: number) => {
    if (isAway) gameState.value.awayTeamTimeoutsLeft = value;
    else gameState.value.homeTeamTimeoutsLeft = value;

    sendGameUpdate();
  };

  return {
    fireGameEvent,
    gameState,
    handleNotification,
    inDanger,
    loading,
    notification,
    preSecondHalfState,
    resetClock,
    resetGameDefaults,
    resetTimeouts,
    sendGameUpdate,
    setDefaultState,
    setGameStateFromSnapshot,
    setInDanger,
    setInitialState,
    setOvertimeState,
    setTimeouts,
    snapShot,
    startClock,
    startSecondHalf,
    stopClock,
    undoStartSecondHalf,
    useTimeout,
  };
});
